import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  Loader,
  User,
  Building2,
  ShieldCheck,
  Lock,
  Mail,
  Phone,
  MapPin,
  Eye,
  EyeOff,
  Globe2,
  Server,
  FileCheck2,
  ChevronRight
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { getApiErrorMessage } from "../lib/apiError";

export default function Register() {
  const navigate = useNavigate();
  const { customerRegister } = useAuth();

  // "private" (Privatkunde) or "business" (Geschäftskunde)
  const [customerType, setCustomerType] = useState("private");

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    passwordConfirm: "",
    company: "",
    vatNumber: "",
    address: "",
    zip: "",
    city: "",
    country: "CH",
    phone: "",
    acceptTerms: false
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  // Password strength calculation
  const getPasswordStrength = (pwd) => {
    if (!pwd) return { score: 0, label: "Nicht angegeben", color: "bg-slate-700" };
    let score = 0;
    if (pwd.length >= 8) score += 1;
    if (pwd.length >= 12) score += 1;
    if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) score += 1;
    if (/[0-9]/.test(pwd)) score += 1;
    if (/[^A-Za-z0-9]/.test(pwd)) score += 1;

    if (score <= 2) return { score: 1, label: "Schwach", color: "bg-rose-500", text: "text-rose-400" };
    if (score <= 4) return { score: 2, label: "Mittel", color: "bg-amber-500", text: "text-amber-400" };
    return { score: 3, label: "Sehr stark", color: "bg-emerald-500", text: "text-emerald-400" };
  };

  const strength = getPasswordStrength(form.password);

  const validateForm = () => {
    if (!form.firstName.trim()) return "Vorname ist erforderlich.";
    if (!form.lastName.trim()) return "Nachname ist erforderlich.";
    if (!form.email.trim()) return "E-Mail-Adresse ist erforderlich.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      return "Bitte geben Sie eine gültige E-Mail-Adresse ein.";
    }

    if (customerType === "business") {
      if (!form.company.trim()) return "Firmenname ist für Geschäftskunden erforderlich.";
      if (!form.address.trim()) return "Strasse & Hausnummer sind für Geschäftskunden erforderlich.";
      if (!form.zip.trim() || !form.city.trim()) return "PLZ und Ort sind für Geschäftskunden erforderlich.";
    }

    if (form.password.length < 8) {
      return "Das Passwort muss mindestens 8 Zeichen lang sein.";
    }
    if (form.password !== form.passwordConfirm) {
      return "Die Passwörter stimmen nicht überein.";
    }
    if (!form.acceptTerms) {
      return "Bitte stimmen Sie den AGB und den Datenschutzbestimmungen zu.";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      await customerRegister({
        customerType,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        company: customerType === "business" ? form.company.trim() : "",
        vatNumber: customerType === "business" ? form.vatNumber.trim() : "",
        address: form.address.trim(),
        zip: form.zip.trim(),
        city: form.city.trim(),
        country: form.country || "CH",
        phone: form.phone.trim()
      });

      setSuccess("Konto erfolgreich erstellt! Sie werden weitergeleitet...");
      window.setTimeout(() => {
        const pendingRedirect = sessionStorage.getItem("redwork_pending_redirect");
        if (pendingRedirect) {
          sessionStorage.removeItem("redwork_pending_redirect");
          navigate(pendingRedirect, { replace: true });
          return;
        }

        const pendingProd = sessionStorage.getItem("redwork_pending_product_id");
        if (pendingProd) {
          navigate(`/dashboard/products/${pendingProd}`, { replace: true });
          return;
        }

        navigate("/dashboard", { replace: true });
      }, 1200);
    } catch (err) {
      setError(getApiErrorMessage(err, "Registrierung fehlgeschlagen"));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  return (
    <div className="min-h-screen bg-[#07090F] text-slate-100 flex flex-col justify-between selection:bg-[#E63946] selection:text-white relative overflow-hidden py-12 px-4 sm:px-6 lg:px-8">
      {/* Background Ambient Glows */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[350px] bg-gradient-to-b from-[#E63946]/10 via-[#FF7A00]/5 to-transparent blur-3xl pointer-events-none -z-10" />
      <div className="absolute -top-32 -right-32 w-96 h-96 bg-red-600/10 rounded-full blur-3xl pointer-events-none -z-10" />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl pointer-events-none -z-10" />

      {/* Main Container */}
      <div className="w-full max-w-4xl mx-auto">
        {/* Top Branding / Breadcrumb */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-4 group">
            <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#E63946] to-[#b32b38] flex items-center justify-center font-black text-white text-base shadow-lg shadow-red-500/30 group-hover:scale-105 transition">
              R
            </span>
            <span className="text-2xl font-black tracking-tight text-white">
              Red<span className="text-[#E63946]">WORK</span>
              <span className="text-xs uppercase text-slate-400 font-mono ml-2 tracking-widest">CH</span>
            </span>
          </Link>
          <h1 className="text-3xl sm:text-4xl font-black text-white tracking-tight">
            Kundenkonto eröffnen
          </h1>
          <p className="mt-2 text-sm sm:text-base text-slate-400 max-w-lg mx-auto">
            Souveräne Schweizer Cloud & Webhosting-Infrastruktur. Schnelle Aktivierung, DSG-konform und hochverfügbar.
          </p>
        </div>

        {/* Card */}
        <div className="rounded-[32px] bg-[#0F1422]/90 backdrop-blur-xl border border-white/10 p-6 sm:p-10 shadow-2xl relative">
          {/* Top highlight line */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-48 h-[2px] bg-gradient-to-r from-transparent via-[#E63946] to-transparent" />

          {/* Customer Type Switcher (Pill Selector) */}
          <div className="mb-8">
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">
              Kontotyp auswählen
            </label>
            <div className="grid grid-cols-2 gap-3 p-1.5 rounded-2xl bg-white/5 border border-white/10">
              <button
                type="button"
                onClick={() => setCustomerType("private")}
                className={`flex items-center justify-center gap-2.5 py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-200 ${
                  customerType === "private"
                    ? "bg-gradient-to-r from-[#E63946] to-[#c72635] text-white shadow-lg shadow-red-500/25"
                    : "text-slate-400 hover:text-white hover:bg-white/5"
                }`}
              >
                <User size={18} />
                <span>Privatkunde</span>
              </button>

              <button
                type="button"
                onClick={() => setCustomerType("business")}
                className={`flex items-center justify-center gap-2.5 py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-200 ${
                  customerType === "business"
                    ? "bg-gradient-to-r from-[#E63946] to-[#c72635] text-white shadow-lg shadow-red-500/25"
                    : "text-slate-400 hover:text-white hover:bg-white/5"
                }`}
              >
                <Building2 size={18} />
                <span>Geschäftskunde (Firma / KMU)</span>
              </button>
            </div>
            <div className="mt-2 text-xs text-slate-400 flex items-center gap-1.5 px-1">
              <ShieldCheck size={14} className="text-emerald-400 flex-shrink-0" />
              <span>
                {customerType === "business"
                  ? "Rechnungen mit ausgewiesener MWST, UID-Nummer und Firmenanschrift für Buchhaltung & KMU."
                  : "Ideal für Einzelpersonen, Entwickler und private Webprojekte."}
              </span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* BUSINESS SPECIFIC FIELDS */}
            {customerType === "business" && (
              <div className="p-5 rounded-2xl bg-white/[0.03] border border-white/10 space-y-4 animate-in fade-in-50 duration-300">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#FF7A00]">
                  <Building2 size={16} />
                  <span>Unternehmensdaten</span>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                      Firmenname *
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={form.company}
                      onChange={handleChange}
                      placeholder="Muster IT Solutions AG"
                      required={customerType === "business"}
                      disabled={loading}
                      className="w-full rounded-xl border border-white/10 bg-[#07090F]/70 px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-[#E63946] focus:ring-1 focus:ring-[#E63946] outline-none transition disabled:opacity-50"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                      UID / MWST-Nummer <span className="text-slate-500 font-normal">(optional)</span>
                    </label>
                    <input
                      type="text"
                      name="vatNumber"
                      value={form.vatNumber}
                      onChange={handleChange}
                      placeholder="CHE-123.456.789 MWST"
                      disabled={loading}
                      className="w-full rounded-xl border border-white/10 bg-[#07090F]/70 px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-[#E63946] focus:ring-1 focus:ring-[#E63946] outline-none transition disabled:opacity-50"
                    />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                      Strasse & Hausnummer *
                    </label>
                    <div className="relative">
                      <MapPin size={16} className="absolute left-3.5 top-3 text-slate-500" />
                      <input
                        type="text"
                        name="address"
                        value={form.address}
                        onChange={handleChange}
                        placeholder="Bahnhofstrasse 10"
                        required={customerType === "business"}
                        disabled={loading}
                        className="w-full rounded-xl border border-white/10 bg-[#07090F]/70 pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-[#E63946] focus:ring-1 focus:ring-[#E63946] outline-none transition disabled:opacity-50"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 sm:col-span-1">
                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                        PLZ *
                      </label>
                      <input
                        type="text"
                        name="zip"
                        value={form.zip}
                        onChange={handleChange}
                        placeholder="8001"
                        required={customerType === "business"}
                        disabled={loading}
                        className="w-full rounded-xl border border-white/10 bg-[#07090F]/70 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-[#E63946] focus:ring-1 focus:ring-[#E63946] outline-none transition disabled:opacity-50 text-center"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                        Ort *
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={form.city}
                        onChange={handleChange}
                        placeholder="Zürich"
                        required={customerType === "business"}
                        disabled={loading}
                        className="w-full rounded-xl border border-white/10 bg-[#07090F]/70 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-[#E63946] focus:ring-1 focus:ring-[#E63946] outline-none transition disabled:opacity-50"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* CONTACT PERSON SECTION */}
            <div>
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">
                <User size={16} />
                <span>{customerType === "business" ? "Ansprechpartner / Kontaktperson" : "Persönliche Daten"}</span>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                    Vorname *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={form.firstName}
                    onChange={handleChange}
                    placeholder="Max"
                    required
                    disabled={loading}
                    className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-[#E63946] focus:ring-1 focus:ring-[#E63946] outline-none transition disabled:opacity-50"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                    Nachname *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={form.lastName}
                    onChange={handleChange}
                    placeholder="Muster"
                    required
                    disabled={loading}
                    className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-[#E63946] focus:ring-1 focus:ring-[#E63946] outline-none transition disabled:opacity-50"
                  />
                </div>
              </div>
            </div>

            {/* EMAIL & PHONE */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                  E-Mail-Adresse (Login) *
                </label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3.5 top-3.5 text-slate-500" />
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="name@ihre-domain.ch"
                    required
                    disabled={loading}
                    className="w-full rounded-xl border border-white/10 bg-white/5 pl-10 pr-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-[#E63946] focus:ring-1 focus:ring-[#E63946] outline-none transition disabled:opacity-50"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                  Telefonnummer <span className="text-slate-500 font-normal">(für 2FA & Notfall-Support)</span>
                </label>
                <div className="relative">
                  <Phone size={16} className="absolute left-3.5 top-3.5 text-slate-500" />
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="+41 79 123 45 67"
                    disabled={loading}
                    className="w-full rounded-xl border border-white/10 bg-white/5 pl-10 pr-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-[#E63946] focus:ring-1 focus:ring-[#E63946] outline-none transition disabled:opacity-50"
                  />
                </div>
              </div>
            </div>

            {/* PRIVATE ADDRESS OPTIONAL OR COMPACT */}
            {customerType === "private" && (
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                    Adresse <span className="text-slate-500 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={form.address}
                    onChange={handleChange}
                    placeholder="Strasse und Hausnummer"
                    disabled={loading}
                    className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-[#E63946] focus:ring-1 focus:ring-[#E63946] outline-none transition disabled:opacity-50"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2 sm:col-span-1">
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                      PLZ
                    </label>
                    <input
                      type="text"
                      name="zip"
                      value={form.zip}
                      onChange={handleChange}
                      placeholder="8000"
                      disabled={loading}
                      className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-[#E63946] focus:ring-1 focus:ring-[#E63946] outline-none transition disabled:opacity-50 text-center"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                      Ort
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={form.city}
                      onChange={handleChange}
                      placeholder="Zürich"
                      disabled={loading}
                      className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-[#E63946] focus:ring-1 focus:ring-[#E63946] outline-none transition disabled:opacity-50"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* PASSWORD SECURITY */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                <Lock size={16} />
                <span>Sicherheit & Zugangsdaten</span>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                    Passwort vergeben *
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={form.password}
                      onChange={handleChange}
                      placeholder="Mindestens 8 Zeichen"
                      required
                      disabled={loading}
                      className="w-full rounded-xl border border-white/10 bg-white/5 pl-4 pr-11 py-3 text-sm text-white placeholder:text-slate-500 focus:border-[#E63946] focus:ring-1 focus:ring-[#E63946] outline-none transition disabled:opacity-50"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-3.5 text-slate-400 hover:text-white transition"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                    Passwort bestätigen *
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="passwordConfirm"
                      value={form.passwordConfirm}
                      onChange={handleChange}
                      placeholder="Passwort wiederholen"
                      required
                      disabled={loading}
                      className="w-full rounded-xl border border-white/10 bg-white/5 pl-4 pr-11 py-3 text-sm text-white placeholder:text-slate-500 focus:border-[#E63946] focus:ring-1 focus:ring-[#E63946] outline-none transition disabled:opacity-50"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3.5 top-3.5 text-slate-400 hover:text-white transition"
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Password Strength Indicator */}
              {form.password && (
                <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5 space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-400">Passwort-Sicherheit:</span>
                    <span className={`font-semibold ${strength.text}`}>{strength.label}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-1.5 h-1.5">
                    <div className={`rounded-full ${strength.score >= 1 ? strength.color : "bg-slate-800"}`} />
                    <div className={`rounded-full ${strength.score >= 2 ? strength.color : "bg-slate-800"}`} />
                    <div className={`rounded-full ${strength.score >= 3 ? strength.color : "bg-slate-800"}`} />
                  </div>
                </div>
              )}
            </div>

            {/* TERMS & CONDITIONS CHECKBOX */}
            <div className="pt-2">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  name="acceptTerms"
                  checked={form.acceptTerms}
                  onChange={handleChange}
                  className="mt-1 h-4 w-4 rounded border-white/20 bg-white/5 text-[#E63946] focus:ring-red-500 focus:ring-offset-0 cursor-pointer"
                />
                <span className="text-xs text-slate-400 leading-relaxed group-hover:text-slate-300 transition">
                  Ich akzeptiere die{" "}
                  <Link to="/legal" target="_blank" className="text-[#FF7A00] underline hover:text-[#ff922d]">
                    Allgemeinen Geschäftsbedingungen (AGB)
                  </Link>{" "}
                  und die{" "}
                  <Link to="/privacy" target="_blank" className="text-[#FF7A00] underline hover:text-[#ff922d]">
                    Datenschutzerklärung
                  </Link>{" "}
                  von RedWORK.ch. Die Datenverarbeitung erfolgt nach Schweizer Datenschutzgesetz (DSG).
                </span>
              </label>
            </div>

            {/* Error Message */}
            {error && (
              <div className="rounded-2xl bg-red-500/10 border border-red-500/30 p-4 text-xs text-red-400 flex items-start gap-3">
                <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
                <span className="font-medium">{error}</span>
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="rounded-2xl bg-emerald-500/10 border border-emerald-500/30 p-4 text-xs text-emerald-400 flex items-start gap-3">
                <CheckCircle2 size={18} className="mt-0.5 flex-shrink-0" />
                <span className="font-medium">{success}</span>
              </div>
            )}

            {/* Submit Action */}
            <div className="pt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <button
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 rounded-xl bg-gradient-to-r from-[#E63946] to-[#c72635] px-8 py-3.5 text-sm font-bold text-white shadow-xl shadow-red-500/25 hover:shadow-red-500/40 hover:brightness-110 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {loading ? (
                  <>
                    <Loader size={18} className="animate-spin" />
                    <span>Wird registriert...</span>
                  </>
                ) : (
                  <>
                    <span>Konto jetzt erstellen</span>
                    <ArrowRight size={18} />
                  </>
                )}
              </button>

              <div className="text-center sm:text-right">
                <p className="text-xs text-slate-400">
                  Bereits registriert?{" "}
                  <Link
                    to="/login"
                    className="font-bold text-[#FF7A00] hover:text-[#ffa043] transition ml-1 inline-flex items-center gap-0.5"
                  >
                    Hier anmelden
                    <ChevronRight size={14} />
                  </Link>
                </p>
              </div>
            </div>
          </form>

          {/* Swiss Trust & Security Badges Bar */}
          <div className="mt-10 pt-6 border-t border-white/10 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div className="flex flex-col items-center p-2 rounded-xl bg-white/[0.02]">
              <Server size={20} className="text-[#E63946] mb-1.5" />
              <span className="text-xs font-bold text-white">Tier-IV Rechenzentrum</span>
              <span className="text-[11px] text-slate-400">Zürich & Glattbrugg CH</span>
            </div>
            <div className="flex flex-col items-center p-2 rounded-xl bg-white/[0.02]">
              <ShieldCheck size={20} className="text-emerald-400 mb-1.5" />
              <span className="text-xs font-bold text-white">100% DSG-konform</span>
              <span className="text-[11px] text-slate-400">Schweizer Datenschutz</span>
            </div>
            <div className="flex flex-col items-center p-2 rounded-xl bg-white/[0.02]">
              <Lock size={20} className="text-[#FF7A00] mb-1.5" />
              <span className="text-xs font-bold text-white">256-Bit SSL Verschlüsselt</span>
              <span className="text-[11px] text-slate-400">Höchster Sicherheitsstandard</span>
            </div>
            <div className="flex flex-col items-center p-2 rounded-xl bg-white/[0.02]">
              <FileCheck2 size={20} className="text-blue-400 mb-1.5" />
              <span className="text-xs font-bold text-white">Sofortige Bereitstellung</span>
              <span className="text-[11px] text-slate-400">Automatischer cPanel-Zugang</span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer copyright */}
      <div className="text-center text-xs text-slate-500 mt-8">
        &copy; {new Date().getFullYear()} RedWORK Switzerland. Alle Rechte vorbehalten.
      </div>
    </div>
  );
}
